import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleSync } from '@/hooks/useModuleSync';

export interface MarketingCampaign {
  id: string;
  name: string;
  description: string | null;
  campaign_type: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget: number;
  spent: number;
  target_audience: string | null;
  goals: string | null;
  project_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  campaign_id: string | null;
  platform: string;
  content: string | null;
  media_url: string | null;
  scheduled_date: string | null;
  published_date: string | null;
  status: string;
  engagement_likes: number;
  engagement_comments: number;
  engagement_shares: number;
  engagement_reach: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingAd {
  id: string;
  campaign_id: string | null;
  name: string;
  platform: string;
  ad_type: string;
  status: string;
  budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  conversions: number;
  start_date: string | null;
  end_date: string | null;
  target_url: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarketingExpense {
  id: string;
  campaign_id: string | null;
  ad_id: string | null;
  booking_id: string | null;
  description: string;
  amount: number;
  expense_date: string;
  category: string | null;
  created_by: string | null;
  created_at: string;
}

interface MarketingData {
  campaigns: MarketingCampaign[];
  socialPosts: SocialPost[];
  ads: MarketingAd[];
  expenses: MarketingExpense[];
}

export const fetchMarketingDataFromDb = async (): Promise<MarketingData> => {
  const [campaignsRes, postsRes, adsRes, expensesRes] = await Promise.all([
    supabase.from('marketing_campaigns').select('*').order('created_at', { ascending: false }),
    supabase.from('marketing_social_posts').select('*').order('scheduled_date', { ascending: false }),
    supabase.from('marketing_ads').select('*').order('created_at', { ascending: false }),
    supabase.from('marketing_expenses').select('*').order('expense_date', { ascending: false }),
  ]);

  if (campaignsRes.error) throw campaignsRes.error;
  if (postsRes.error) throw postsRes.error;
  if (adsRes.error) throw adsRes.error;
  if (expensesRes.error) throw expensesRes.error;

  return {
    campaigns: (campaignsRes.data || []) as MarketingCampaign[],
    socialPosts: (postsRes.data || []) as SocialPost[],
    ads: (adsRes.data || []) as MarketingAd[],
    expenses: (expensesRes.data || []) as MarketingExpense[],
  };
};

export const useMarketing = () => {
  const queryClient = useQueryClient();
  const { user, initialized } = useAuthContext();
  const { emitEvent } = useModuleSync();
  const { data, isLoading: loading, refetch: fetchAll } = useQuery({
    queryKey: ['marketing'],
    queryFn: fetchMarketingDataFromDb,
    enabled: !!user && initialized,
  });

  const campaigns = data?.campaigns ?? [];
  const socialPosts = data?.socialPosts ?? [];
  const ads = data?.ads ?? [];
  const expenses = data?.expenses ?? [];

  // Campaign CRUD
  const createCampaign = async (data: Partial<MarketingCampaign>) => {
    const insertData = { ...data, created_by: user?.id };
    const { data: newCampaign, error } = await supabase
      .from('marketing_campaigns')
      .insert(insertData as any)
      .select()
      .single();
    
    if (!error && newCampaign) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
      emitEvent({ type: 'create', module: 'marketing_campaigns', data: newCampaign, projectId: (newCampaign as any).project_id });
    }
    return { data: newCampaign, error };
  };

  const updateCampaign = async (id: string, data: Partial<MarketingCampaign>) => {
    const { data: updated, error } = await supabase
      .from('marketing_campaigns')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (!error && updated) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { data: updated, error };
  };

  const deleteCampaign = async (id: string) => {
    const { error } = await supabase
      .from('marketing_campaigns')
      .delete()
      .eq('id', id);
    
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { error };
  };

  // Social Post CRUD
  const createSocialPost = async (data: Partial<SocialPost>) => {
    const insertData = { ...data, created_by: user?.id };
    const { data: newPost, error } = await supabase
      .from('marketing_social_posts')
      .insert(insertData as any)
      .select()
      .single();
    
    if (!error && newPost) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { data: newPost, error };
  };

  const updateSocialPost = async (id: string, data: Partial<SocialPost>) => {
    const { data: updated, error } = await supabase
      .from('marketing_social_posts')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (!error && updated) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { data: updated, error };
  };

  const deleteSocialPost = async (id: string) => {
    const { error } = await supabase
      .from('marketing_social_posts')
      .delete()
      .eq('id', id);
    
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { error };
  };

  // Ad CRUD
  const createAd = async (data: Partial<MarketingAd>) => {
    const insertData = { ...data, created_by: user?.id };
    const { data: newAd, error } = await supabase
      .from('marketing_ads')
      .insert(insertData as any)
      .select()
      .single();
    
    if (!error && newAd) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { data: newAd, error };
  };

  const updateAd = async (id: string, data: Partial<MarketingAd>) => {
    const { data: updated, error } = await supabase
      .from('marketing_ads')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (!error && updated) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { data: updated, error };
  };

  const deleteAd = async (id: string) => {
    const { error } = await supabase
      .from('marketing_ads')
      .delete()
      .eq('id', id);
    
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { error };
  };

  // Expense CRUD
  const createExpense = async (data: Partial<MarketingExpense>) => {
    const insertData = { ...data, created_by: user?.id };
    const { data: newExpense, error } = await supabase
      .from('marketing_expenses')
      .insert(insertData as any)
      .select()
      .single();
    
    if (!error && newExpense) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
      emitEvent({ type: 'create', module: 'marketing_expenses', data: newExpense });
    }
    return { data: newExpense, error };
  };

  const deleteExpense = async (id: string) => {
    const { error } = await supabase
      .from('marketing_expenses')
      .delete()
      .eq('id', id);
    
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['marketing'] });
    }
    return { error };
  };

  // Stats
  const totalBudget = campaigns.reduce((sum, c) => sum + Number(c.budget), 0);
  const totalSpent = campaigns.reduce((sum, c) => sum + Number(c.spent), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'aktiv');
  const plannedPosts = socialPosts.filter(p => p.status === 'geplant');
  const activeAds = ads.filter(a => a.status === 'aktiv');
  const totalImpressions = ads.reduce((sum, a) => sum + a.impressions, 0);
  const totalClicks = ads.reduce((sum, a) => sum + a.clicks, 0);
  const totalConversions = ads.reduce((sum, a) => sum + a.conversions, 0);
  const totalEngagement = socialPosts.reduce((sum, p) => 
    sum + p.engagement_likes + p.engagement_comments + p.engagement_shares, 0);

  return {
    campaigns,
    socialPosts,
    ads,
    expenses,
    loading,
    refetch: fetchAll,
    // Campaign methods
    createCampaign,
    updateCampaign,
    deleteCampaign,
    // Social post methods
    createSocialPost,
    updateSocialPost,
    deleteSocialPost,
    // Ad methods
    createAd,
    updateAd,
    deleteAd,
    // Expense methods
    createExpense,
    deleteExpense,
    // Stats
    totalBudget,
    totalSpent,
    activeCampaigns,
    plannedPosts,
    activeAds,
    totalImpressions,
    totalClicks,
    totalConversions,
    totalEngagement,
  };
};
